import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import './Customer.css';

export const CustomerCreate = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
    }
  }, [isAuthenticated, companyId, token, logout]);

  // Form States
  const [names, setNames] = useState('');
  const [emails, setEmails] = useState('');
  const [contactNumber1s, setContactNumber1s] = useState('');
  const [contactNumber2s, setContactNumber2s] = useState('');
  const [addressline1s, setAddressLine1s] = useState('');
  const [addressline2s, setAddressLine2s] = useState('');
  const [location, setLocation] = useState('');
  const [customernote, setCustomernote] = useState('');
  const [proofNo, setProofNo] = useState('');
  const [cautionDeposite, setCautionDeposite] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [successMessages, setSuccessMessages] = useState('');
  const [errorMessages, setErrorMessages] = useState('');
  const [RentDate, setRentDate] = useState(new Date().toISOString().split('T')[0]);
  const [DateOfJoining, setDateOfJoining] = useState(new Date().toISOString().split('T')[0]);
  const [DateOfLeaving, setDateOfLeaving] = useState(new Date().toISOString().split('T')[0]);

  // ID Card
  const [cardTypeOptions, setcardTypeOptions] = useState([]);
  const [selectedcardType, setSelectedcardType] = useState('');

  const fetchCardTypes = async () => {
    try {
      const response = await fetchData('CustomerApi', `/${companyId}/${companyId}`, token);
      const options = response.map((Card) => ({
        value: Card.id,
        label: Card.cardName,
      }));
      setcardTypeOptions(options);
    } catch (error) {
      console.error('Error fetching Customer Types:', error);
    }
  };

  useEffect(() => {
    fetchCardTypes();
  }, []);

  // Room
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [roomName, setRoomName] = useState([]);

  const fetchRoomTypes = async () => {
    try {
      const response = await fetchData('CustomerApi', `/RommDetails/${companyId}`);
      if (response.responseCode === '1111') {
        const options = response.response_data.map((rooms) => ({
          value: rooms.ID,
          label: rooms.Name,
        }));
        setRoomName(response.response_data);
        setRoomTypeOptions(options);
      }
    } catch (error) {
      console.error('Error fetching Room Types:', error);
    }
  };

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const handleroomChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedRoomType(selectedOption);
      const selectedRoomData = roomName.find((customer) => customer.ID === selectedOption.value);
      if (selectedRoomData) {
        setRentAmount(selectedRoomData.RentAmount);
      }
    }
  };

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!names || !contactNumber1s) {
      setErrorMessages('Name and Contact Number are required!');
      setIsLoading(false);
      return;
    }

    try {
      const CustomerCreateData = {
        CompanyId: companyId,
        RoomId: selectedRoomType.value,
        IdCardId: selectedcardType.value,
        Name: names,
        ContactNumber1: contactNumber1s,
        ContactNumber2: contactNumber2s,
        AddressLine1: addressline1s,
        AdressLine2: addressline2s,
        Location: location,
        Email: emails,
        CustomerRemark: customernote,
        ProofNo: proofNo,
        CautionDeposite: cautionDeposite,
        RentAmount: rentAmount,
        RentDate: RentDate,
        DateOfJoining: DateOfJoining,
        DateOfLeaving: DateOfLeaving,
        CreatedBy: userId,
        CreatedDate: new Date(),
      };

      postData('CustomerApi', CustomerCreateData, token)
        .then(() => {
          setSuccessMessages('Customer created successfully!');
          setErrorMessages('');
          setTimeout(() => {
            history.push('/customerIndex');
          }, 1500);
        })
        .catch((error) => {
          console.error('Create error:', error);
          setErrorMessages('Failed to create customer');
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Create error:', error);
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="form-container">
        <div className="form-card">
          {/* Header */}
          <div className="form-header">
            <h1 className="form-title">Create Customer</h1>
            <div className="form-actions">
              <Link to="/customerindex" className="btn-back">
                <ArrowBackIcon fontSize="small" />
                Back to List
              </Link>
              <button className="btn-save" onClick={submitData} disabled={isLoading}>
                {isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <>
                    <SaveIcon fontSize="small" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Messages */}
          {errorMessages && <div className="message error">{errorMessages}</div>}
          {successMessages && <div className="message success">{successMessages}</div>}

          {/* Form Body */}
          <div className="form-body">
            {/* Personal Information */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  className="form-input"
                  value={names}
                  onChange={(e) => setNames(e.target.value)}
                  type="text"
                  placeholder="Enter full name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact No. 1 *</label>
                <input
                  className="form-input"
                  value={contactNumber1s}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, '');
                    if (input.length <= 15) setContactNumber1s(input);
                  }}
                  type="text"
                  placeholder="Primary contact"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact No. 2</label>
                <input
                  className="form-input"
                  value={contactNumber2s}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, '');
                    if (input.length <= 15) setContactNumber2s(input);
                  }}
                  type="text"
                  placeholder="Secondary contact"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  type="email"
                  placeholder="Email address"
                />
              </div>
            </div>

            {/* Address */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Address Line 1</label>
                <input
                  className="form-input"
                  value={addressline1s}
                  onChange={(e) => setAddressLine1s(e.target.value)}
                  type="text"
                  placeholder="Street address"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address Line 2</label>
                <input
                  className="form-input"
                  value={addressline2s}
                  onChange={(e) => setAddressLine2s(e.target.value)}
                  type="text"
                  placeholder="Apartment, suite, etc."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  className="form-input"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  type="text"
                  placeholder="City / Area"
                />
              </div>
            </div>

            {/* ID & Room */}
            <div className="form-row">
              <div className="form-group form-select">
                <label className="form-label">ID Proof</label>
                <Select
                  value={selectedcardType}
                  onChange={setSelectedcardType}
                  options={cardTypeOptions}
                  placeholder="Select ID type"
                  isSearchable
                />
              </div>
              {selectedcardType && (
                <div className="form-group">
                  <label className="form-label">Proof Number</label>
                  <input
                    className="form-input"
                    value={proofNo}
                    onChange={(e) => setProofNo(e.target.value)}
                    type="text"
                    placeholder="ID number"
                  />
                </div>
              )}
              <div className="form-group form-select">
                <label className="form-label">Room</label>
                <Select
                  value={selectedRoomType}
                  onChange={handleroomChange}
                  options={roomTypeOptions}
                  placeholder="Select room"
                  isSearchable
                />
              </div>
              {selectedRoomType && (
                <div className="form-group">
                  <label className="form-label">Rent Amount</label>
                  <input
                    className="form-input"
                    value={rentAmount}
                    onChange={(e) => setRentAmount(e.target.value)}
                    type="text"
                    placeholder="Monthly rent"
                  />
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date of Joining</label>
                <input
                  className="form-input"
                  value={DateOfJoining}
                  onChange={(e) => setDateOfJoining(e.target.value)}
                  type="date"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Leaving</label>
                <input
                  className="form-input"
                  value={DateOfLeaving}
                  onChange={(e) => setDateOfLeaving(e.target.value)}
                  type="date"
                />
              </div>
              {selectedRoomType && (
                <div className="form-group">
                  <label className="form-label">Rent Date</label>
                  <input
                    className="form-input"
                    value={RentDate}
                    onChange={(e) => setRentDate(e.target.value)}
                    type="date"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Caution Deposit</label>
                <input
                  className="form-input"
                  value={cautionDeposite}
                  onChange={(e) => setCautionDeposite(e.target.value)}
                  type="text"
                  placeholder="Deposit amount"
                />
              </div>
            </div>

            {/* Remarks */}
            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label">Customer Remarks</label>
                <input
                  className="form-input"
                  value={customernote}
                  onChange={(e) => setCustomernote(e.target.value)}
                  type="text"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
